/* eslint-disable @typescript-eslint/no-explicit-any */
import DOMPurify from 'isomorphic-dompurify';
import {
  ASPECT_RATIO_UXL_FIELD_MAPPING,
  APP_TILE_SQUARE_VARATIONS,
  IMAGE_CACHE_DOMAIN,
  PRIMARY_MEDIA,
  SECONDARY_MEDIA,
  TERTIARY_MEDIA,
} from '../constants/preview-constants';

export enum BadgeVariation {
  'Inline' = 'inline',
  'Overlay' = 'overlay',
}

export const getImageWithDomain = (imgSrc: string): string => {
  if (imgSrc) {
    return `${IMAGE_CACHE_DOMAIN + imgSrc}`;
  } else {
    return '';
  }
};

export const decodeHtml = (text: string): string => {
  if (typeof document !== 'undefined') {
    const fragment = DOMPurify.sanitize(text, {
      ALLOWED_TAGS: [
        'b',
        'strong',
        'i',
        'em',
        'u',
        's',
        'del',
        'sub',
        'sup',
        'mark',
        'small',
        'big',
        'ul',
        'ol',
        'li',
        'dl',
        'dt',
        'dd',
        'table',
        'tr',
        'td',
        'th',
        'thead',
        'tbody',
        'tfoot',
        'colgroup',
        'col',
        'caption',
        'p',
        'br',
        'hr',
        'blockquote',
        'pre',
        'a',
        'code',
        'span',
        'div',
        'img',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
      ],
      RETURN_DOM: true,
    }) as HTMLElement;
    // Convert the DOM Fragment to string
    let sanitizedHtml = fragment.innerHTML ?? '';
    // Replace sequences of multiple spaces with corresponding number of non-breaking spaces
    sanitizedHtml = sanitizedHtml.replace(/ {2,}/g, match => '&nbsp;'.repeat(match.length));
    return sanitizedHtml;
  }
  return text;
};
/**
 * @description Image data structure refers to the variation of the
 * component and the URL should have variation name so that it will
 * pick the exact image url from product specific response.
 */
export const ImageRenditionsDataStructure = {
  aspectRatio: {
    dynamicMedia: ['wideHorizontal', 'square', 'classicVertical'],
  },
  dynamicMedia: {
    altText: 'Dominican Republic Destination Image',
    assetPath: 'https://s7d1.scene7.com/is/image/marriotts7prod/pdt-Dominican-Republic-453698474706472',
    enableSmartCrop: true,
    dynamic: true,
    renditions: [],
    damPath:
      '/content/dam/marriott-digital/continents/cala/en_us/destinations/photo/2023-photoshoot/unlimited/assets/pdt-Dominican-Republic-453698474706472.jpg',
  },
  dynamicMediaSquare: {
    altText: 'Dominican Republic Destination Image',
    assetPath: 'https://s7d1.scene7.com/is/image/marriotts7prod/pdt-Dominican-Republic-453698474706472',
    enableSmartCrop: true,
    dynamic: true,
    renditions: [
      {
        renditionPath:
          'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-1096489-gettyimage-16746:Square?wid=365&fit=constrain',
        mediaValue: '992px',
        width: 0,
        dynamic: true,
        damPath:
          '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-1096489-gettyimage-16746.jpg',
        mediaQuery: 'min-width',
        height: 0,
      },
      {
        renditionPath:
          'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-1096489-gettyimage-16746:Square?wid=344&fit=constrain',
        mediaValue: '576px',
        width: 0,
        dynamic: true,
        damPath:
          '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-1096489-gettyimage-16746.jpg',
        mediaQuery: 'min-width',
        height: 0,
      },
      {
        renditionPath:
          'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-1096489-gettyimage-16746:Square?wid=332&fit=constrain',
        mediaValue: '576px',
        width: 0,
        dynamic: true,
        damPath:
          '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-1096489-gettyimage-16746.jpg',
        mediaQuery: 'max-width',
        height: 0,
      },
    ],
    damPath:
      '/content/dam/marriott-digital/continents/cala/en_us/destinations/photo/2023-photoshoot/unlimited/assets/pdt-Dominican-Republic-453698474706472.jpg',
  },
  dynamicMediaTall: {
    damPath:
      '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
    assetPath: 'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502',
    altText: 'Sunrise over the clouds',
    dynamic: true,
    enableSmartCrop: true,
    renditions: [
      {
        renditionPath:
          'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Classic-Ver?wid=380&fit=constrain, https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Classic-Ver?wid=760&fit=constrain 2x',
        damPath:
          '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
        mediaValue: '1200px',
        mediaQuery: 'min-width',
        width: 0,
        height: 0,
        smartCropType: null,
        dynamic: true,
      },
      {
        renditionPath:
          'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Classic-Ver?wid=480&fit=constrain, https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Classic-Ver?wid=960&fit=constrain 2x',
        damPath:
          '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
        mediaValue: '768px',
        mediaQuery: 'min-width',
        width: 0,
        height: 0,
        smartCropType: null,
        dynamic: true,
      },
      {
        renditionPath:
          'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Classic-Ver?wid=575&fit=constrain, https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Classic-Ver?wid=1150&fit=constrain 2x',
        damPath:
          '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
        mediaValue: '767px',
        mediaQuery: 'max-width',
        width: 0,
        height: 0,
        smartCropType: null,
        dynamic: true,
      },
    ],
  },
  dynamicMediaWide: {
    damPath:
      '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
    assetPath: 'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502',
    altText: 'Sunrise over the clouds',
    dynamic: true,
    enableSmartCrop: true,
    renditions: [
      {
        renditionPath:
          'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Wide-Hor?wid=570&fit=constrain, https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Wide-Hor?wid=1140&fit=constrain 2x',
        damPath:
          '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
        mediaValue: '1200px',
        mediaQuery: 'min-width',
        width: 0,
        height: 0,
        smartCropType: null,
        dynamic: true,
      },
      {
        renditionPath:
          'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Wide-Hor?wid=480&fit=constrain, https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Wide-Hor?wid=960&fit=constrain 2x',
        damPath:
          '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
        mediaValue: '768px',
        mediaQuery: 'min-width',
        width: 0,
        height: 0,
        smartCropType: null,
        dynamic: true,
      },
      {
        renditionPath:
          'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Wide-Hor?wid=575&fit=constrain, https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Wide-Hor?wid=1150&fit=constrain 2x',
        damPath:
          '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
        mediaValue: '767px',
        mediaQuery: 'max-width',
        width: 0,
        height: 0,
        smartCropType: null,
        dynamic: true,
      },
    ],
  },
  offersCardTile: {
    aspectRatio: {
      backgroundMedia: ['wideHorizontal', 'square'],
    },
    backgroundMediaWide: {
      renditions: [
        {
          renditionPath:
            'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-1096489-gettyimage-16746:Wide-Hor?wid=558&hei=314, https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-1096489-gettyimage-16746:Wide-Hor?wid=1116&hei=628 2x',
          damPath:
            '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-1096489-gettyimage-16746.jpg',
          mediaValue: '992px',
          mediaQuery: 'min-width',
          width: 558,
          height: 314,
          smartCropType: 'wideHorizontal',
          dynamic: true,
        },
        {
          renditionPath:
            'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-1096489-gettyimage-16746:Wide-Hor?wid=558&hei=314, https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-1096489-gettyimage-16746:Wide-Hor?wid=1116&hei=628 2x',
          damPath:
            '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-1096489-gettyimage-16746.jpg',
          mediaValue: '768px',
          mediaQuery: 'min-width',
          width: 558,
          height: 314,
          smartCropType: 'wideHorizontal',
          dynamic: true,
        },
        {
          renditionPath:
            'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-1096489-gettyimage-16746:Wide-Hor?wid=558&hei=314, https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-1096489-gettyimage-16746:Wide-Hor?wid=1116&hei=628 2x',
          damPath:
            '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-1096489-gettyimage-16746.jpg',
          mediaValue: '576px',
          mediaQuery: 'max-width',
          width: 558,
          height: 314,
          smartCropType: 'wideHorizontal',
          dynamic: true,
        },
      ],
    },
    backgroundMediaSquare: {
      renditions: [
        {
          renditionPath:
            'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Square?wid=364&hei=364, https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Square?wid=728&hei=728 2x',
          damPath:
            '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
          mediaValue: '992px',
          mediaQuery: 'min-width',
          width: 364,
          height: 364,
          smartCropType: 'square',
          dynamic: true,
        },
        {
          renditionPath:
            'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Square?wid=364&hei=364, https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Square?wid=728&hei=728 2x',
          damPath:
            '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
          mediaValue: '768px',
          mediaQuery: 'min-width',
          width: 364,
          height: 364,
          smartCropType: 'square',
          dynamic: true,
        },
        {
          renditionPath:
            'https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Square?wid=364&hei=364, https://s7d1.scene7.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Square?wid=728&hei=728 2x',
          damPath:
            '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
          mediaValue: '576px',
          mediaQuery: 'max-width',
          width: 364,
          height: 364,
          smartCropType: 'square',
          dynamic: true,
        },
      ],
    },
  },
  offersFeatureBanner: {
    aspectRatio: {
      backgroundMedia: ['wideHorizontal', 'classicHorizontal'],
      dynamicMediaLogo: ['classicHorizontal'],
    },
    backgroundMedia: {
      renditions: [
        {
          renditionPath:
            'https://cache.marriott.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Wide-Hor?wid=1440&hei=810, https://cache.marriott.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Wide-Hor?wid=2880&hei=1620 2x',
          mediaValue: '992px',
          width: 1440,
          dynamic: true,
          smartCropType: 'wideHorizontal',
          damPath:
            '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
          mediaQuery: 'min-width',
          height: 810,
        },
        {
          renditionPath:
            'https://cache.marriott.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Classic-Hor?wid=240&hei=196, https://cache.marriott.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Classic-Hor?wid=480&hei=392 2x',
          mediaValue: '768px',
          width: 240,
          dynamic: true,
          smartCropType: 'classicHorizontal',
          damPath:
            '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
          mediaQuery: 'min-width',
          height: 196,
        },
        {
          renditionPath:
            'https://cache.marriott.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Classic-Hor?wid=240&hei=196, https://cache.marriott.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Classic-Hor?wid=480&hei=392 2x',
          mediaValue: '767.5px',
          width: 240,
          dynamic: true,
          smartCropType: 'classicHorizontal',
          damPath:
            '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
          mediaQuery: 'max-width',
          height: 196,
        },
      ],
    },
  },
  offersSignInModal: {
    aspectRatio: {
      backgroundMedia: ['wideHorizontal', 'classicHorizontal'],
    },
    backgroundMedia: {
      renditions: [
        {
          renditionPath:
            'https://s7d1.scene7.com/is/image/marriotts7prod/pdt-JordHammond-HongKong-739569223059559:Wide-Hor?wid=1440&hei=810, https://s7d1.scene7.com/is/image/marriotts7prod/pdt-JordHammond-HongKong-739569223059559:Wide-Hor?wid=2880&hei=1620 2x',
          mediaValue: '992px',
          width: 1440,
          dynamic: true,
          smartCropType: 'wideHorizontal',
          damPath:
            '/content/dam/marriott-digital/continents/greater-china/en_us/destinations/photo/2023-photoshoot/unlimited/assets/pdt-JordHammond-HongKong-739569223059559.jpg',
          mediaQuery: 'min-width',
          height: 810,
        },
        {
          renditionPath:
            'https://s7d1.scene7.com/is/image/marriotts7prod/pdt-JordHammond-HongKong-739569223059559:Wide-Hor?wid=1440&hei=810, https://s7d1.scene7.com/is/image/marriotts7prod/pdt-JordHammond-HongKong-739569223059559:Wide-Hor?wid=2880&hei=1620 2x',
          mediaValue: '768px',
          width: 1440,
          dynamic: true,
          smartCropType: 'wideHorizontal',
          damPath:
            '/content/dam/marriott-digital/continents/greater-china/en_us/destinations/photo/2023-photoshoot/unlimited/assets/pdt-JordHammond-HongKong-739569223059559.jpg',
          mediaQuery: 'min-width',
          height: 810,
        },
        {
          renditionPath:
            'https://s7d1.scene7.com/is/image/marriotts7prod/pdt-JordHammond-HongKong-739569223059559:Classic-Hor?wid=240&hei=196, https://s7d1.scene7.com/is/image/marriotts7prod/pdt-JordHammond-HongKong-739569223059559:Classic-Hor?wid=480&hei=392 2x',
          mediaValue: '576px',
          width: 240,
          dynamic: true,
          smartCropType: 'classicHorizontal',
          damPath:
            '/content/dam/marriott-digital/continents/greater-china/en_us/destinations/photo/2023-photoshoot/unlimited/assets/pdt-JordHammond-HongKong-739569223059559.jpg',
          mediaQuery: 'max-width',
          height: 196,
        },
      ],
    },
  },
  webHeroCarouselCard: {
    aspectRatio: {
      backgroundMedia: ['wideHorizontal'],
      dynamicMediaLogo: ['classicHorizontal'],
    },
    backgroundMedia: {
      renditions: [
        {
          renditionPath:
            'https://cache.marriott.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Wide-Hor?wid=1440&hei=810, https://cache.marriott.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Wide-Hor?wid=2880&hei=1620 2x',
          mediaValue: '992px',
          width: 1440,
          dynamic: true,
          smartCropType: 'wideHorizontal',
          damPath:
            '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
          mediaQuery: 'min-width',
          height: 810,
        },
        {
          renditionPath:
            'https://cache.marriott.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Classic-Hor?wid=240&hei=196, https://cache.marriott.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Classic-Hor?wid=480&hei=392 2x',
          mediaValue: '768px',
          width: 240,
          dynamic: true,
          smartCropType: 'classicHorizontal',
          damPath:
            '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
          mediaQuery: 'min-width',
          height: 196,
        },
        {
          renditionPath:
            'https://cache.marriott.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Classic-Hor?wid=240&hei=196, https://cache.marriott.com/is/image/marriotts7stage/bonvoy-gettyimages-1041814538-24502:Classic-Hor?wid=480&hei=392 2x',
          mediaValue: '767.5px',
          width: 240,
          dynamic: true,
          smartCropType: 'classicHorizontal',
          damPath:
            '/content/dam/marriott-digital/bonvoy/global/en_us/photo/unlimited/assets/bonvoy-gettyimages-1041814538-24502.jpg',
          mediaQuery: 'max-width',
          height: 196,
        },
      ],
    },
  },
  modal: {
    aspectRatio: {
      backgroundMedia: ['wideHorizontal'],
    },
    backgroundMedia: {
      renditions: [
        {
          renditionPath:
            'https://cache.marriott.com/content/dam/marriott-renditions/dm-static-renditions/bonvoy/global/en_us/photo/unlimited/assets/1028766452-29492-wide-hor.jpg',
          mediaValue: '992px',
          width: 0,
          dynamic: false,
          smartCropType: 'wideHorizontal',
          damPath:
            'https://cache.marriott.com/content/dam/marriott-renditions/dm-static-renditions/bonvoy/global/en_us/photo/unlimited/assets/1028766452-29492-wide-hor.jpg',
          mediaQuery: 'min-width',
          height: 0,
        },
        {
          renditionPath:
            'https://cache.marriott.com/content/dam/marriott-renditions/dm-static-renditions/bonvoy/global/en_us/photo/unlimited/assets/1028766452-29492-wide-hor.jpg',
          mediaValue: '768px',
          width: 0,
          dynamic: false,
          smartCropType: 'wideHorizontal',
          damPath:
            'https://cache.marriott.com/content/dam/marriott-renditions/dm-static-renditions/bonvoy/global/en_us/photo/unlimited/assets/1028766452-29492-wide-hor.jpg',
          mediaQuery: 'min-width',
          height: 0,
        },
        {
          renditionPath:
            'https://cache.marriott.com/content/dam/marriott-renditions/dm-static-renditions/bonvoy/global/en_us/photo/unlimited/assets/1028766452-29492-wide-hor.jpg',
          mediaValue: '767.5px',
          width: 0,
          dynamic: false,
          smartCropType: 'wideHorizontal',
          damPath:
            'https://cache.marriott.com/content/dam/marriott-renditions/dm-static-renditions/bonvoy/global/en_us/photo/unlimited/assets/1028766452-29492-wide-hor.jpg',
          mediaQuery: 'max-width',
          height: 0,
        },
      ],
    },
  },
};

/**
 * @description Getting the aspect ratio based on the app tile square
 * variaiton authored for media data function.
 */
export const getAsptRatioForMediaData = (appTileSquareVariation: string) => {
  switch (appTileSquareVariation) {
    case APP_TILE_SQUARE_VARATIONS.squarev1:
    case APP_TILE_SQUARE_VARATIONS.squarev2:
    case APP_TILE_SQUARE_VARATIONS.squarev3:
      return 'Square';

    case APP_TILE_SQUARE_VARATIONS.tall:
      return 'Classic-Ver';

    case APP_TILE_SQUARE_VARATIONS.wide:
    default:
      return 'Wide-Hor';
  }
};

/**
 * @description Getting the dynamic media information from image renditions data structure based on the app tile square
 * variaiton authored for media data function.
 */
export const getModelForMediaData = (appTileSquareVariation: string) => {
  switch (appTileSquareVariation) {
    case APP_TILE_SQUARE_VARATIONS?.squarev1:
    case APP_TILE_SQUARE_VARATIONS?.squarev2:
    case APP_TILE_SQUARE_VARATIONS?.squarev3:
      return ImageRenditionsDataStructure?.dynamicMediaSquare;

    case APP_TILE_SQUARE_VARATIONS?.tall:
      return ImageRenditionsDataStructure?.dynamicMediaTall;

    case APP_TILE_SQUARE_VARATIONS?.wide:
      return ImageRenditionsDataStructure?.dynamicMediaWide;

    default:
      return ImageRenditionsDataStructure?.dynamicMedia;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getMediaSource = (offersDataMedia: any, expectedSource: any, aspectRatio?: any): string => {
  //updated logic
  if (expectedSource == PRIMARY_MEDIA) {
    let primaryMatchCount = 0;
    for (let i = 0; i <= aspectRatio?.length; i++) {
      if (offersDataMedia?.primaryImage?.imageUrls?.[aspectRatio[i]]) {
        primaryMatchCount++;
      }
    }
    if (primaryMatchCount) {
      return PRIMARY_MEDIA;
    } else {
      return 'model';
    }
  } else {
    if (offersDataMedia?.secondaryImage?.imageSrc) {
      return SECONDARY_MEDIA;
    } else {
      return 'model';
    }
  }
};

export const getPosition = (string: string, subString: string, index: number) => {
  return string.split(subString, index).join(subString).length;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getImagePath = (ren: any, imagePath: string) => {
  let referencePath = ren?.renditionPath.includes(',')
    ? ren.renditionPath.slice(0, ren.renditionPath.indexOf(','))
    : ren?.renditionPath;
  referencePath = referencePath.includes('https') ? referencePath : `${IMAGE_CACHE_DOMAIN}${referencePath}`;
  const urlParams = new URL(referencePath).searchParams;
  const width = ren?.width && ren?.width !== 0 ? ren?.width : urlParams.get('wid');
  const height = ren?.height && ren?.height !== 0 ? ren?.height : urlParams.get('hei') ?? '';
  const fit = urlParams.get('fit');
  const rendition =
    IMAGE_CACHE_DOMAIN + imagePath + '?wid=' + width + (height ? '&hei=' + height : '') + (fit ? '&fit=' + fit : '');
  const rendition2x =
    IMAGE_CACHE_DOMAIN +
    imagePath +
    '?wid=' +
    2 * width +
    (height ? '&hei=' + 2 * height : '') +
    (fit ? '&fit=' + fit : '');
  return `${rendition}, ${rendition2x} 2x`;
};

/**
 * @description Getting image aspect ratio css class based on the variation.
 */
export const getAspectRatioClass = (media: any, mediaSource: string, asptRatio?: string) => {
  const mediaData =
    mediaSource == PRIMARY_MEDIA ? media?.primaryImage : mediaSource == SECONDARY_MEDIA ? media?.secondaryImage : null;
  const imgAspectRatio = asptRatio ?? ASPECT_RATIO_UXL_FIELD_MAPPING?.['Wide-Hor'];
  const key = imgAspectRatio as keyof typeof ASPECT_RATIO_UXL_FIELD_MAPPING;
  const mediaImgUrl = mediaData?.imageUrls?.[ASPECT_RATIO_UXL_FIELD_MAPPING?.[key]];
  const aspectRatioClass =
    mediaImgUrl === '' || mediaImgUrl === null || mediaImgUrl === undefined || mediaSource === SECONDARY_MEDIA
      ? ASPECT_RATIO_UXL_FIELD_MAPPING?.[key]
      : '';
  return aspectRatioClass;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getMediaData = (
  media: any,
  model: any,
  mediaSource: string,
  expectedMedia: string,
  asptRatio?: string
) => {
  const mediaData =
    mediaSource == PRIMARY_MEDIA ? media?.primaryImage : mediaSource == SECONDARY_MEDIA ? media?.secondaryImage : null;
  const imgAspectRatio = asptRatio ?? ASPECT_RATIO_UXL_FIELD_MAPPING?.['Wide-Hor'];
  const renditionsArr = [];
  let imgSrcPath = '';
  if (expectedMedia == PRIMARY_MEDIA) {
    for (let i = 0; i < model?.renditions?.length; i++) {
      const ren = model?.renditions[i];
      const aspectRatio = ren?.renditionPath?.slice(
        getPosition(ren?.renditionPath, ':', 2) + 1,
        ren?.renditionPath.indexOf('?')
      );
      const key = aspectRatio as keyof typeof ASPECT_RATIO_UXL_FIELD_MAPPING;
      const imagePath = mediaData?.imageUrls?.[ASPECT_RATIO_UXL_FIELD_MAPPING?.[key]];
      const renditionObj = {
        renditionPath: getImagePath(ren, imagePath),
        mediaValue: ren?.mediaValue,
        width: ren?.width,
        dynamic: ren?.dynamic,
        mediaQuery: ren?.mediaQuery,
        height: ren?.height,
        damPath: '',
      };
      imagePath && renditionsArr.push(renditionObj);
    }
  }
  const key = imgAspectRatio as keyof typeof ASPECT_RATIO_UXL_FIELD_MAPPING;
  const mediaImgUrl = mediaData?.imageUrls?.[ASPECT_RATIO_UXL_FIELD_MAPPING?.[key]];
  imgSrcPath =
    mediaSource === PRIMARY_MEDIA
      ? mediaImgUrl !== '' && mediaImgUrl !== null && mediaImgUrl !== undefined
        ? mediaImgUrl
        : media?.primaryImage?.imageSrc
      : mediaSource === SECONDARY_MEDIA
      ? media?.secondaryImage?.imageSrc
      : '';
  return {
    renditions: renditionsArr,
    fileReferenceBackgroundImage:
      mediaSource == PRIMARY_MEDIA
        ? media?.primaryImage?.imageSrc
        : mediaSource == SECONDARY_MEDIA
        ? media?.secondaryImage?.imageSrc
        : '',
    altText:
      mediaSource == PRIMARY_MEDIA
        ? media?.primaryImage?.alternateDescription
        : mediaSource == SECONDARY_MEDIA
        ? media?.secondaryImage?.alternateDescription
        : '',
    dynamic: true,
    assetPath: imgSrcPath
      ? `${IMAGE_CACHE_DOMAIN}${imgSrcPath}`
      : renditionsArr?.length
      ? renditionsArr[0]?.renditionPath?.slice(0, getPosition(renditionsArr[0]?.renditionPath, ':', 2))
      : mediaData?.imageSrc
      ? `${IMAGE_CACHE_DOMAIN}${mediaData?.imageSrc}`
      : model?.assetPath,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getMediaDataSecondary = (media: any, model: any, mediaSource: string) => {
  return {
    renditions: [],
    fileReferenceBackgroundImage: mediaSource == SECONDARY_MEDIA ? media?.secondaryImage?.imageSrc : '',
    altText:
      mediaSource == PRIMARY_MEDIA
        ? media?.primaryImage?.alternateDescription
        : mediaSource == SECONDARY_MEDIA
        ? media?.secondaryImage?.alternateDescription
        : '',
    dynamic: true,
    assetPath: media?.secondaryImage?.imageSrc
      ? `${IMAGE_CACHE_DOMAIN}${media?.secondaryImage?.imageSrc}`
      : media?.secondaryImage?.id && typeof window !== 'undefined'
      ? `${IMAGE_CACHE_DOMAIN}${window?.atob(media?.secondaryImage?.id)}`
      : model?.assetPath,
  };
};

export const getMediaDataTertiary = (media: any, model: any, mediaSource: string) => {
  return {
    renditions: [],
    fileReferenceBackgroundImage: mediaSource == TERTIARY_MEDIA ? media?.tertiaryImage?.imageSrc : '',
    altText:
      mediaSource == PRIMARY_MEDIA
        ? media?.primaryImage?.alternateDescription
        : mediaSource == TERTIARY_MEDIA
        ? media?.tertiaryImage?.alternateDescription
        : '',
    dynamic: true,
    assetPath: media?.tertiaryImage?.imageSrc
      ? `${IMAGE_CACHE_DOMAIN}${media?.tertiaryImage?.imageSrc}`
      : media?.tertiaryImage?.id && typeof window !== 'undefined'
      ? `${IMAGE_CACHE_DOMAIN}${window?.atob(media?.tertiaryImage?.id)}`
      : model?.assetPath,
  };
};

// Utility function to return prop value based on author mode
export const getPropValue = (
  authorMode: boolean,
  nodeValue: Node | string | number | object | any,
  modelValue: Node | string | number | object | any
) => (authorMode ? modelValue : nodeValue);

//For removing tags
export const removeParentTagRTE = (textRTE: string): string => {
  return textRTE?.substring(textRTE?.indexOf('>') + 1, textRTE?.lastIndexOf('<')) || textRTE;
};
